import { useEffect, useState } from "react";
import { Search, Star } from "lucide-react";
import { formatMoney } from "@mavatech/shared";
import { Dialog, Input } from "@/components/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { keepPreviousData } from "@tanstack/react-query";
import {
  SupplierChips,
  SupplierTags,
  shownPrices,
  useDebounced,
  useSupplierFilter,
} from "./SupplierFilter";

export interface PickedArticle {
  id: string;
  name: string;
  unit: string;
  salePriceCents: number;
  purchasePriceCents: number | null;
}

interface PriceRow {
  id: string;
  name: string;
  unit: string;
  salePriceCents: number;
  sub: string;
  prices: {
    listPriceCents: number | null;
    netPriceCents: number | null;
    supplier: { id: string; name: string } | null;
  }[];
  listNetCents: number | null;
  purchaseNetCents: number | null;
}

function cheapestListCents(
  prices: { listPriceCents: number | null }[],
): number | null {
  const vals = prices
    .map((p) => p.listPriceCents)
    .filter((n): n is number => n != null);
  return vals.length ? Math.min(...vals) : null;
}

export function ArticlePicker({
  open,
  onOpenChange,
  onPick,
  initialSearch,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onPick: (a: PickedArticle) => void;
  /** Übernimmt einen bereits eingetippten Suchbegriff (z. B. aus dem kleinen Vorschlag im
   * Beschreibungsfeld) beim Öffnen — für "in großer Ansicht weitersuchen". */
  initialSearch?: string;
}) {
  const [search, setSearch] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  useEffect(() => {
    if (open && initialSearch) setSearch(initialSearch);
  }, [open, initialSearch]);
  const [supplierId, setSupplierId] = useSupplierFilter();
  const dSearch = useDebounced(search, 250);
  const list = trpc.article.list.useQuery(
    {
      search: dSearch || undefined,
      favoritesOnly: favoritesOnly || undefined,
      supplierId: supplierId || undefined,
    },
    { enabled: open, placeholderData: keepPreviousData },
  );
  const settling = search !== dSearch || list.isFetching;
  const recent = trpc.article.recent.useQuery(undefined, {
    enabled: open && !search && !favoritesOnly && !supplierId,
  });

  const rows: PriceRow[] =
    search || favoritesOnly || supplierId || !recent.data?.length
      ? (list.data ?? []).map((a) => ({
          id: a.id,
          name: a.name,
          unit: a.unit,
          salePriceCents: a.salePriceCents,
          sub: a.ownNumber ?? a.category ?? "",
          prices: a.prices,
          listNetCents: shownPrices(
            a.prices,
            supplierId || undefined,
            a.bestPurchasePriceCents,
          ).list,
          purchaseNetCents: shownPrices(
            a.prices,
            supplierId || undefined,
            a.bestPurchasePriceCents,
          ).purchase,
        }))
      : recent.data.map((r) => ({
          id: r.article.id,
          name: r.article.name,
          unit: r.article.unit,
          salePriceCents: r.article.salePriceCents,
          sub: "zuletzt verwendet",
          prices: r.article.prices.map((p) => ({ ...p, supplier: null })),
          listNetCents: cheapestListCents(r.article.prices),
          purchaseNetCents: r.article.bestPurchasePriceCents ?? null,
        }));

  const anyMulti = rows.some(
    (a) => a.prices.filter((p) => p.supplier).length > 1,
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Artikel übernehmen"
      full
      guardClose={false}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            autoFocus
            placeholder="Artikel suchen (Name, Nummer, EAN) …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => setFavoritesOnly((v) => !v)}
          title="Nur Favoriten (z. B. Pauschalen) anzeigen"
          className={cn(
            "flex items-center gap-1.5 px-3 py-2 rounded border text-sm flex-shrink-0 transition-colors",
            favoritesOnly
              ? "bg-accent/15 border-accent/30 text-accent"
              : "bg-bg-input border-border text-text-secondary hover:text-text-primary",
          )}
        >
          <Star className={cn("w-3.5 h-3.5", favoritesOnly && "fill-accent")} />{" "}
          Favoriten
        </button>
      </div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <SupplierChips value={supplierId} onChange={setSupplierId} />
        {settling && (
          <span className="text-2xs text-text-tertiary">sucht …</span>
        )}
      </div>
      <div
        className={cn(
          "max-h-[65vh] overflow-y-auto -mx-1 transition-opacity",
          settling && "opacity-80",
        )}
      >
        {!rows.length ? (
          <p className="text-sm text-text-tertiary p-3">
            {favoritesOnly
              ? 'Noch keine Favoriten. In „Artikel" bei einer Position „Favorit" aktivieren — praktisch für Pauschalen wie eine Anfahrtspauschale.'
              : list.isError
                ? "Die Suche war zu aufwendig. Bitte genauer eingeben, z. B. ein Wort dazuschreiben (Kugelhahn 25)."
                : settling
                  ? "Suche läuft …"
                  : search || supplierId
                  ? "Keine Treffer."
                  : 'Noch keine Artikel. Lege welche unter „Artikel" an oder importiere einen Katalog.'}
          </p>
        ) : (
          rows.map((a) => (
            <button
              key={a.id}
              className="w-full text-left px-3 py-2 rounded hover:bg-bg-hover flex items-center justify-between gap-3"
              onClick={() => {
                onPick({
                  id: a.id,
                  name: a.name,
                  unit: a.unit,
                  salePriceCents: a.salePriceCents,
                  purchasePriceCents: a.purchaseNetCents,
                });
                onOpenChange(false);
              }}
            >
              <span className="min-w-0">
                <span className="block text-sm text-text-primary truncate">
                  {a.name}
                </span>
                <span className="block text-2xs text-text-tertiary">
                  {a.sub}
                </span>
              </span>
              <span className="flex items-center gap-3 flex-shrink-0">
                <span className="text-right leading-tight">
                  <span className="block text-2xs text-text-tertiary">
                    netto / {a.unit}
                  </span>
                  <span className="text-sm text-money text-text-secondary">
                    {a.listNetCents != null ? formatMoney(a.listNetCents) : "—"}
                  </span>
                </span>
                <span className="text-right leading-tight">
                  <span className="block text-2xs text-text-tertiary">
                    Einkauf
                  </span>
                  <span className="text-sm text-money text-text-tertiary">
                    {a.purchaseNetCents != null
                      ? formatMoney(a.purchaseNetCents)
                      : "—"}
                  </span>
                </span>
                <SupplierTags
                  prices={a.prices}
                  supplierId={supplierId}
                  stacked
                  wide={anyMulti}
                />
              </span>
            </button>
          ))
        )}
      </div>
    </Dialog>
  );
}
